import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import SeoHead, { orgSchema, websiteSchema, breadcrumbSchema } from "@/components/SeoHead";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { Loader2, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Insights() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/posts`)
      .then((r) => setPosts(r.data || []))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="bg-[#050505] text-white font-outfit min-h-screen">
      <SeoHead
        title="Insights — Finance commentary & guides"
        description="Australian commercial, business and private credit finance commentary, refinance strategies and developer-finance guides from the OzyLoans team. ACL #485752."
        path="/insights"
        schemas={[
          orgSchema(),
          websiteSchema(),
          breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Insights", path: "/insights" }]),
        ]}
      />
      <SiteHeader />
      <section className="px-6 pt-20 pb-16 max-w-6xl mx-auto">
        <p className="text-xs uppercase tracking-[0.4em] text-[#E50914] mb-4">Insights · OzyLoans</p>
        <h1 className="font-bebas text-5xl sm:text-6xl tracking-tight leading-[0.95] max-w-3xl">
          Finance commentary,<br/>not finance fluff.
        </h1>
        <p className="text-zinc-400 text-lg mt-5 max-w-2xl">
          Refinance strategies, private-lender-exit playbooks, developer-finance commentary and Australian commercial-lending insights from the OzyLoans desk.
        </p>
      </section>

      <section className="border-t border-zinc-900 px-6 py-12 max-w-6xl mx-auto">
        {loading && (
          <div className="text-zinc-500 flex items-center gap-2 py-8" data-testid="insights-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        )}
        {!loading && posts.length === 0 && (
          <div className="rounded-2xl border border-zinc-800 bg-[#0a0a0a] p-10 text-center" data-testid="insights-empty">
            <p className="text-zinc-400 text-lg">Our first articles are landing soon.</p>
            <p className="text-zinc-600 text-sm mt-2">Subscribe by{" "}
              <a href="mailto:mark@ozyloans.com.au?subject=Subscribe%20to%20Insights" className="text-[#E50914] hover:underline">emailing us</a>{" "}
              and we'll send each new piece direct.
            </p>
          </div>
        )}
        {!loading && posts.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {posts.map((p) => (
              <Link
                key={p.id}
                to={`/insights/${p.slug}`}
                data-testid={`insight-card-${p.slug}`}
                className="group block bg-[#0a0a0a] border border-zinc-800 rounded-2xl p-7 hover:border-[#E50914]/50 hover:bg-[#0c0c0c] transition-colors"
              >
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#E50914] mb-3">{p.category}</p>
                <h2 className="text-xl font-semibold text-white mb-2 leading-snug">{p.title}</h2>
                <p className="text-zinc-500 text-sm leading-relaxed line-clamp-3">{p.excerpt}</p>
                <span className="inline-flex items-center gap-1 text-zinc-600 group-hover:text-[#E50914] text-xs uppercase tracking-widest mt-5 transition-colors">
                  Read <ArrowRight className="w-3 h-3" />
                </span>
              </Link>
            ))}
          </div>
        )}
      </section>
      <SiteFooter />
    </div>
  );
}
